import { IAction } from '@Root/types'

export const SpeakerVoiceActionTypes = {
  FETCH_SPEAKER_VOICES: '@@speakerVoice/FETCH_SPEAKER_VOICES',
  FETCH_SPEAKER_VOICES_SUCCESS: '@@speakerVoice/FETCH_SUCCESS',
  FETCH_SPEAKER_VOICES_FAILURE: '@@speakerVoice/FETCH_FAILURE',

  FETCH_LANGUAGES: '@@speakerVoice/FETCH_LANGUAGES',
  FETCH_LANGUAGES_SUCCESS: '@@speakerVoice/FETCH_LANGUAGES_SUCCESS',
  FETCH_LANGUAGES_FAILURE: '@@speakerVoice/FETCH_LANGUAGES_FAILURE',

  RESET_SHOULD_CHECK_AUTO_FILL: '@@speakerVoice/RESET_SHOULD_CHECK_AUTO_FILL',
  SET_SHOULD_CHECK_AUTO_FILL: '@@speakerVoice/SET_SHOULD_CHECK_AUTO_FILL',

  UPDATE_SPEAKER_VOICE: '@@speakerVoice/UPDATE_SPEAKER_VOICE',
  UPDATE_SPEAKER_VOICE_SUCCESS: '@@speakerVoice/UPDATE_SPEAKER_VOICE_SUCCESS',
  UPDATE_SPEAKER_VOICE_FAILURE: '@@speakerVoice/UPDATE_SPEAKER_VOICE_FAILURE',

  REMOVE_SPEAKER_VOICES_FROM_WORKSPACE: '@@speakerVoice/REMOVE_SPEAKER_VOICE_FROM_WORKSPACE',
  REMOVE_SPEAKER_VOICES_FROM_WORKSPACE_SUCCESS: '@@speakerVoice/REMOVE_SPEAKER_VOICE_FROM_WORKSPACE_SUCCESS',
  REMOVE_SPEAKER_VOICES_FROM_WORKSPACE_FAILURE: '@@speakerVoice/REMOVE_SPEAKER_VOICE_FROM_WORKSPACE_FAILURE',

  CREATE_SPEAKER_VOICE: '@@speakerVoice/CREATE_SPEAKER_VOICE',
  CREATE_SPEAKER_VOICE_SUCCESS: '@@speakerVoice/CREATE_SPEAKER_VOICE_SUCCESS',
  CREATE_SPEAKER_VOICE_FAILURE: '@@speakerVoice/CREATE_SPEAKER_VOICE_FAILURE',

  GENERATE_DESIGN_SAMPLE: '@@speakerVoice/GENERATE_DESIGN_SAMPLE',
  GENERATE_DESIGN_SAMPLE_SUCCESS: '@@speakerVoice/GENERATE_DESIGN_SAMPLE_SUCCESS',
  GENERATE_DESIGN_SAMPLE_FAILURE: '@@speakerVoice/GENERATE_DESIGN_SAMPLE_FAILURE',
  RESET_GENERATED_DESIGN_SAMPLE: '@@speakerVoice/RESET_GENERATED_DESIGN_SAMPLE',

  CHANGE_SELECTED_SPEAKER_VOICES: '@@speakerVoice/CHANGE_SELECTED_SPEAKER_VOICES',
  RESET_SELECTED_SPEAKER_VOICES: '@@speakerVoice/RESET_SELECTED_SPEAKER_VOICES',

  SET_SEARCH_TERM: '@@speakerVoice/SET_SEARCH_TERM',

  SET_OPENED_SPEAKER_VOICE_ID: '@@speakerVoice/SET_OPENED_SPEAKER_VOICE_ID',
  RESET_OPENED_SPEAKER_VOICE_ID: '@@speakerVoice/RESET_OPENED_SPEAKER_VOICE_ID',

  SET_IS_NEW_SPEAKER_VOICE: '@@speakerVoice/SET_IS_NEW_SPEAKER_VOICE',
  RESET_IS_NEW_SPEAKER_VOICE: '@@speakerVoice/RESET_IS_NEW_SPEAKER_VOICE',

  CHANGE_SELECTED_LANGUAGE: '@@speakerVoice/CHANGE_SELECTED_LANGUAGE',
  SET_SELECTED_LANGUAGES: '@@speakerVoice/SET_SELECTED_LANGUAGES',
}

export type TSpeakerVoiceLanguages = { [languageCode: string]: ILanguageData }
export type TSpeakerVoiceLanguagesNamesAndCountries = { [languageName: string]: string[] } // { name: [countries] }
export type TSpeakerVoiceUsedLanguages = { [languageName: string]: number } // { name: useCount }

export interface ISpeakerVoiceState {
  readonly speakerVoices: ISpeakerVoice[]
  readonly languages: TSpeakerVoiceLanguages
  readonly openedSpeakerVoiceId?: string
  readonly isNewSpeakerVoice: boolean
  readonly selectedIds: string[]
  readonly usedLanguages: TSpeakerVoiceUsedLanguages
  readonly selectedLanguagesNames: string[]
  readonly hasLoadedInitially: boolean
  readonly loading: boolean
  readonly isGeneratingDesignSample: boolean
  readonly generatedDesignSampleUrl?: string
  readonly generatedDesignSampleError?: string | null
  readonly page: {
    readonly currentPage: number
    readonly totalCount: number
    readonly totalPages: number
    readonly shouldCheckAutoFill: boolean
    readonly searchTerm?: string
  }
}

export interface ISpeakerVoice {
  readonly id: string
  readonly attributes: ISpeakerVoiceAttributes
}

export interface ISpeakerVoiceData {
  readonly id: string
  readonly type: 'speakerVoice',
  readonly attributes: ISpeakerVoiceAttributes
}

export interface IDraftSpeakerVoice extends Partial<ISpeakerVoiceAttributes> {
  readonly id: string
  readonly color: string
}

export interface ISpeakerVoiceAttributes {
  readonly name: string
  readonly description: string
  readonly gender: string
  readonly color: string
  readonly thumbnailUrl: string
  readonly trainingUrls: string[]
  readonly private: boolean
  readonly languageName: string
  readonly languageAccent: string
  readonly ageGroup: string
  readonly service?: 'elevenlabs' | 'odub'
  readonly odubModelId?: string
  readonly createdAt: string
  readonly updatedAt: string
}

export interface IFetchSpeakerVoicesPayload {
  readonly workspaceId: number
  readonly includeUsedLanguages?: boolean
  readonly page?: number
  readonly perPage?: number
  readonly searchTerm?: string
}

export interface IFetchSpeakerVoicesSuccessResponseBody {
  readonly data: ISpeakerVoiceData[]
  readonly currentPage: number
  readonly totalCount: number
  readonly totalPages: number
  readonly usedLanguages?: TSpeakerVoiceUsedLanguages
}

export type IFetchLanguagesSuccessResponseBody = TSpeakerVoiceLanguages

export interface ILanguageData {
  readonly name: string
  readonly country?: string
}

export interface IUpdateSpeakerVoicePayload extends Partial<ISpeakerVoiceAttributes> {
  readonly id: string
}

export interface IUpdateSpeakerVoiceSuccessResponseBody {
  readonly data: ISpeakerVoiceData
}

export interface IRemoveSpeakerVoicesFromWorkspacePayload {
  readonly speakerVoiceIds: string[]
  readonly workspaceId: number
  readonly preventNotification?: boolean
}

export interface IRemoveSpeakerVoicesFromWorkspaceSuccessResponseBody {
  readonly speakerVoiceIds: string[]
}

export interface ICreateSpeakerVoicePayload extends Partial<ISpeakerVoiceAttributes> {
  readonly workspaceId: number
  readonly openAfterCreation?: boolean
}

export interface ICreateSpeakerVoiceSuccessResponseBody {
  readonly data: ISpeakerVoiceData
  readonly openAfterCreation?: boolean
}

export interface IGenerateDesignSamplePayload {
  readonly workspaceId: number
  readonly text: string
  readonly description: string
  readonly languageName?: string
}

export interface IGenerateDesignSampleSuccessResponseBody {
  readonly audioUrl: string
  readonly durationSeconds?: number
}

export interface IGenerateDesignSampleJobResponseBody {
  readonly jobId: string
  readonly status: string
}

export interface IGenerateDesignSampleJobStatusResponseBody {
  readonly status: string
  readonly audioUrl?: string
  readonly durationSeconds?: number
}

export interface IGenerateDesignSampleFailurePayload {
  readonly message: string
}

export interface IChangeSelectedSpeakerVoicesPayload {
  readonly speakerVoiceId: string
  readonly selectSpeakerVoice: boolean
}

export interface ISetSearchTermPayload {
  readonly searchTerm?: string
}

export interface ISetOpenedSpeakerVoiceIdPayload {
  readonly speakerVoiceId?: string
}

export interface ISetIsNewSpeakerVoicePayload {
  readonly isNew: boolean
}

export interface IChangeSelectedLanguageNamePayload {
  readonly languageName: string
  readonly select: boolean
}

export interface ISetSelectedLanguagesNamesPayload {
  readonly selectedLanguagesNames: string[]
}

export type TSpeakerVoiceActionTypes =
  IAction<ISpeakerVoice>
